import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { PlatePreview } from "@/components/PlatePreview";
import { useI18n } from "@/i18n";

const SAMPLE = {
  individual: { top: "", bottom: "RPM 3D PRINT", color: "#EC4899" },
  invisible: {},
};

const ProductCard = ({ p, i, onSelect }) => {
  const { t, money } = useI18n();
  const [view, setView] = useState(p.image ? "photo" : "preview");
  const txt = t(`products.items.${p.id}`);
  const price = money(p.price).replace(/[,.]00/, "");

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.42, delay: i * 0.08 }}
      className="rpm-card rpm-card-hover flex flex-col overflow-hidden"
      data-testid={`product-card-${p.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden border-b border-zinc-200 bg-zinc-100">
        {view === "photo" && p.image ? (
          <img
            src={p.image}
            alt={p.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
            style={{ objectPosition: "center 55%" }}
            data-testid={`product-${p.id}-photo`}
          />
        ) : (
          <div className="rpm-grid-bg flex h-full w-full items-center justify-center p-6 sm:p-10">
            <PlatePreview type={p.id === "invisible" ? "invisible" : "individual"} {...SAMPLE[p.id]} hiddenLabel={t("config.holderHidden")} className="w-full" />
          </div>
        )}
        {i === 0 && (
          <span className="absolute left-4 top-4 rounded-full bg-[var(--rpm-lime)] px-3 py-1 font-mono-rpm text-[11px] font-medium uppercase tracking-wider text-black">
            {t("products.badge")}
          </span>
        )}
        {p.image && (
          <div className="absolute bottom-3 right-3 inline-flex rounded-xl border border-zinc-200 bg-white p-0.5 shadow-sm">
            {["photo", "preview"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={`h-8 rounded-[10px] px-3 text-xs font-medium transition-colors ${view === v ? "bg-black text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
                data-testid={`product-${p.id}-view-${v}`}
              >
                {t(`products.${v}`)}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold leading-tight sm:text-2xl" data-testid={`product-${p.id}-name`}>
              {p.name}
            </h3>
            <div className="mt-1 text-sm text-zinc-600">{txt.local}</div>
          </div>
          <div className="shrink-0 text-right">
            <div className="font-display text-3xl font-bold" data-testid={`product-${p.id}-price`}>
              {price}
            </div>
            {p.pair_price ? (
              <div className="mt-1 inline-block rounded-full bg-[var(--rpm-orange)] px-2.5 py-0.5 text-xs font-bold text-black" data-testid={`product-${p.id}-pair-price`}>
                {t("products.pairDeal", { price: money(p.pair_price).replace(/[,.]00/, "") })}
              </div>
            ) : null}
            {p.unit === "pack" ? <div className="mt-1 text-xs font-medium text-zinc-600">{t("products.packNote")}</div> : null}
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-zinc-700">{txt.description}</p>
        <ul className="mt-4 space-y-2">
          {txt.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black">
                <Check className="h-3 w-3 text-[var(--rpm-lime)]" />
              </span>
              {f}
            </li>
          ))}
        </ul>
        <button
          onClick={() => onSelect(p.id)}
          className={`rpm-btn mt-6 w-full ${i === 0 ? "rpm-btn-orange" : "rpm-btn-lime"}`}
          data-testid={`product-${p.id}-select-button`}
        >
          {t(p.pair_price ? "products.order" : "products.orderFor", { price })} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </motion.article>
  );
};

export const Products = ({ products = [], onSelect }) => {
  const { t } = useI18n();
  return (
    <section id="products" className="rpm-section bg-white py-16 sm:py-20 lg:py-24" data-testid="products-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="rpm-label mb-3">{t("products.label")}</div>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("products.title")}</h2>
          </div>
          <p className="max-w-sm text-sm text-zinc-600">{t("products.sub")}</p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {products.map((p, i) => (
            <ProductCard key={p.id} p={p} i={i} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </section>
  );
};
